import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { useTranslation } from 'react-i18next';
import { TOTAL_KEY, type PivotModel, type ResultColumn } from './pivot';
import { displayFieldName, visuallyHidden } from './fields';

const NO_COLUMN: ResultColumn = { key: '', label: '', type: 'STRING', role: 'FIELD', index: 0 };

export type ReportChartType =
  'BAR' | 'STACKED_BAR' | 'HORIZONTAL_BAR' | 'LINE' | 'AREA' | 'PIE' | 'KPI';

/** A report's `chart` object (`report.schema.json#/$defs/chart`). */
export interface ReportChartOptions {
  readonly type: ReportChartType;
  readonly legend?: 'RIGHT' | 'BOTTOM' | 'NONE';
  readonly showValues?: boolean;
  readonly yFromZero?: boolean;
  readonly donut?: boolean;
  readonly valueIndex?: number;
}

export interface ReportChartProps {
  readonly model: PivotModel;
  readonly chart: ReportChartOptions;
  /** The report's name: the chart's accessible name (C-2). */
  readonly title: string;
  readonly formatCell: (column: ResultColumn, raw: string | null) => string;
  readonly height?: number;
}

interface Series {
  readonly key: string;
  readonly label: string;
  readonly data: (number | null)[];
}

/**
 * The chart of a report (SDD-024 C-1, C-2): a pivot that is drawn. Rows are the categories (nested
 * labels joined for 2–3 levels), the column groups the series, `valueIndex` the value drawn (PIE
 * and KPI draw exactly one; the others one series per column group). `@mui/x-charts` with the
 * theme's palette and formatted tooltips; the chart's `aria-label` is the report's name, and an
 * SR-only table carries the same numbers for a screen reader.
 */
export function ReportChart({
  model,
  chart,
  title,
  formatCell,
  height = 320,
}: ReportChartProps): ReactNode {
  const { t } = useTranslation('patterns');
  const theme = useTheme();
  const palette = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.info.main,
    theme.palette.warning.main,
    theme.palette.success.main,
    theme.palette.error.main,
  ];
  const valueIndex = Math.min(chart.valueIndex ?? 0, Math.max(0, model.values.length - 1));
  const value = model.values[valueIndex];
  const leafDepth = model.rowFields.length;
  const leaves = model.rows.filter((r) => !r.total && r.depth === leafDepth);
  const categories = leaves.map((r) =>
    r.path.map((v, i) => formatCell(model.rowFields[i] ?? NO_COLUMN, v)).join(' / '),
  );
  const seriesGroups = model.columnGroups.filter((g) => !g.total);
  const groups = seriesGroups.length > 0 ? seriesGroups : model.columnGroups;

  const series: Series[] = groups.map((group) => ({
    key: group.key,
    label:
      group.key === TOTAL_KEY
        ? displayFieldName(value?.label ?? '')
        : group.values.map((v, i) => formatCell(model.columnFields[i] ?? NO_COLUMN, v)).join(' · '),
    data: leaves.map((row) => {
      const raw = row.cells[group.key]?.[valueIndex];
      return raw === undefined || raw === null ? null : Number(raw);
    }),
  }));

  const format = (n: number | null): string =>
    value === undefined || n === null ? '' : formatCell(value, String(n));
  const legendHidden = chart.legend === 'NONE';
  const legendPosition =
    chart.legend === 'BOTTOM'
      ? ({ vertical: 'bottom', horizontal: 'center' } as const)
      : ({ vertical: 'middle', horizontal: 'end' } as const);
  const common = {
    height,
    colors: palette,
    hideLegend: legendHidden,
    slotProps: { legend: { position: legendPosition } },
  };

  let drawing: ReactNode;
  switch (chart.type) {
    case 'KPI': {
      const total = model.rows.find((r) => r.total)?.cells[TOTAL_KEY]?.[valueIndex];
      const sum = series[0]?.data.reduce<number>((acc, n) => acc + (n ?? 0), 0) ?? 0;
      drawing = (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }} role="img" aria-label={title}>
          <Typography variant="body2" color="text.secondary">
            {t('reporting.chart.kpiOf', { label: displayFieldName(value?.label ?? '') })}
          </Typography>
          <Typography variant="h3" component="p" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {format(total === undefined || total === null ? sum : Number(total))}
          </Typography>
        </Paper>
      );
      break;
    }
    case 'PIE': {
      const totals = groups.find((g) => g.key === TOTAL_KEY) ?? groups[0];
      const data = leaves.map((row, index) => ({
        id: index,
        label: categories[index] ?? '',
        value: Number(row.cells[totals?.key ?? TOTAL_KEY]?.[valueIndex] ?? 0),
      }));
      drawing = (
        <PieChart
          {...common}
          series={[
            {
              data,
              innerRadius: chart.donut === true ? '55%' : 0,
              valueFormatter: (item) => format(item.value),
              ...(chart.showValues === true ? { arcLabel: (item) => format(item.value) } : {}),
            },
          ]}
        />
      );
      break;
    }
    case 'LINE':
    case 'AREA':
      drawing = (
        <LineChart
          {...common}
          xAxis={[{ scaleType: 'point', data: categories }]}
          yAxis={[
            {
              valueFormatter: (n: number) => format(n),
              ...(chart.yFromZero === false ? {} : { min: 0 }),
            },
          ]}
          series={series.map((s) => ({
            id: s.key,
            label: s.label,
            data: s.data,
            area: chart.type === 'AREA',
            valueFormatter: (n: number | null) => format(n),
          }))}
        />
      );
      break;
    default: {
      const horizontal = chart.type === 'HORIZONTAL_BAR';
      const band = [{ scaleType: 'band' as const, data: categories }];
      const linear = [
        {
          valueFormatter: (n: number) => format(n),
          ...(chart.yFromZero === false ? {} : { min: 0 }),
        },
      ];
      drawing = (
        <BarChart
          {...common}
          layout={horizontal ? 'horizontal' : 'vertical'}
          {...(horizontal ? { yAxis: band, xAxis: linear } : { xAxis: band, yAxis: linear })}
          series={series.map((s) => ({
            id: s.key,
            label: s.label,
            data: s.data,
            ...(chart.type === 'STACKED_BAR' ? { stack: 'total' } : {}),
            valueFormatter: (n: number | null) => format(n),
          }))}
          {...(chart.showValues === true ? { barLabel: 'value' as const } : {})}
        />
      );
    }
  }

  return (
    <Box data-testid="report-chart" data-chart-type={chart.type}>
      <Box role="figure" aria-label={title}>
        {drawing}
      </Box>
      {/* C-2: the same numbers for a screen reader — the chart is a picture of this table. */}
      <Box sx={visuallyHidden}>
        <table aria-label={t('reporting.chart.dataTable')}>
          <thead>
            <tr>
              <th scope="col">
                {model.rowFields.map((f) => displayFieldName(f.label)).join(' / ')}
              </th>
              {series.map((s) => (
                <th key={s.key} scope="col">
                  {s.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {categories.map((category, row) => (
              <tr key={category}>
                <th scope="row">{category}</th>
                {series.map((s) => (
                  <td key={s.key}>{format(s.data[row] ?? null)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
